import React, { useState, useEffect, useContext } from 'react';
import {
  Typography, Card, Switch, Form, Input, Button, Table, Tag, Space, message, Divider,
} from 'antd';
import { MailOutlined, WarningOutlined } from '@ant-design/icons';
import api from '../api';
import { formatTS } from '../utils/time';
import { UserContext } from '../App';

export default function EmailConfigPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';

  const [config, setConfig]   = useState(null);
  const [errors, setErrors]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/email-config');
      setConfig(res.data.config);
      setErrors(res.data.recent_errors || []);
      form.setFieldsValue({
        sender_name: res.data.config?.SenderName || '',
        from_email:  res.data.config?.FromEmail  || '',
      });
    } catch {
      message.error('Failed to load email configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (checked) => {
    try {
      await api.patch('/api/email-config', { is_enabled: checked });
      setConfig((c) => ({ ...c, IsEnabled: checked ? 1 : 0 }));
      message.success(`Email sending ${checked ? 'enabled' : 'disabled'}.`);
    } catch {
      message.error('Failed to update setting.');
    }
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch { return; }

    setSaving(true);
    try {
      await api.patch('/api/email-config', {
        sender_name: values.sender_name,
        from_email:  values.from_email,
      });
      message.success('Email configuration saved.');
      load();
    } catch {
      message.error('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const errorColumns = [
    {
      title: 'Date / Time',
      dataIndex: 'OccurredAt',
      key: 'OccurredAt',
      width: 175,
      render: (v) => formatTS(v),
    },
    {
      title: 'To',
      dataIndex: 'ToEmail',
      key: 'ToEmail',
      width: 220,
    },
    {
      title: 'Subject',
      dataIndex: 'Subject',
      key: 'Subject',
      width: 220,
    },
    {
      title: 'Error',
      dataIndex: 'ErrorMessage',
      key: 'ErrorMessage',
      render: (v) => (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>{v}</Typography.Text>
      ),
    },
  ];

  const isEnabled = !!config?.IsEnabled;

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
        Email Configuration
      </Typography.Title>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        Configure automated email sending. SMTP credentials are set as Azure App Service environment variables
        (<Typography.Text code>EMAIL_SMTP_USERNAME</Typography.Text> and <Typography.Text code>EMAIL_SMTP_PASSWORD</Typography.Text>).
      </Typography.Text>

      <Card
        title={<Space><MailOutlined />Global Settings</Space>}
        loading={loading}
        style={{ marginBottom: 16 }}
      >
        <Space align="center" style={{ marginBottom: 20 }}>
          <Switch
            checked={isEnabled}
            disabled={!isAdmin}
            onChange={isAdmin ? handleToggle : undefined}
          />
          <Typography.Text strong>
            Email sending is currently{' '}
            <Tag color={isEnabled ? 'green' : 'default'}>
              {isEnabled ? 'ENABLED' : 'DISABLED'}
            </Tag>
          </Typography.Text>
        </Space>

        <Divider style={{ margin: '12px 0' }} />

        <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
          <Form.Item
            label="Sender Name"
            name="sender_name"
            tooltip="Display name shown in the From field of sent emails"
          >
            <Input placeholder="QA Dashboard" disabled={!isAdmin} />
          </Form.Item>
          <Form.Item
            label="From Email Address"
            name="from_email"
            tooltip="The email address shown as the sender. Must match or be an alias of the SMTP account."
            rules={[{ type: 'email', message: 'Enter a valid email address' }]}
          >
            <Input placeholder="notifications@superiorcontact.net" disabled={!isAdmin} />
          </Form.Item>

          {isAdmin && (
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" onClick={handleSave} loading={saving}>
                Save
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card
        title={<Space><WarningOutlined style={{ color: '#ff4d4f' }} />Recent Send Errors (last 5)</Space>}
        loading={loading}
      >
        {errors.length === 0 ? (
          <Typography.Text type="secondary">No send errors recorded.</Typography.Text>
        ) : (
          <Table
            rowKey="ID"
            dataSource={errors}
            columns={errorColumns}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
        )}
      </Card>
    </>
  );
}
